import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Table, Typography, Tag, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { ArrowRightOutlined } from '@ant-design/icons';

import { useDataViewPagination } from '../../../shared/dataview/useDataViewPagination.js';
import type { DealListItem, DealStage } from '../api/deals.client.js';

const { Text } = Typography;

const NEXT_STAGE: Partial<Record<DealStage, DealStage>> = {
  New: 'Contacted',
  Contacted: 'Negotiation',
  Negotiation: 'Won',
};

const STAGE_COLORS: Record<string, string> = {
  New: 'blue',
  Contacted: 'orange',
  Negotiation: 'gold',
  Won: 'green',
  Lost: 'red',
};

export interface DealListViewProps {
  deals: DealListItem[];
  selectedIds: ReadonlySet<string>;
  isUpdating: boolean;
  onToggleSelected: (dealId: string) => void;
  onMoveDeal: (dealId: string, toStage: DealStage) => void;
}

function getContactName(deal: DealListItem): string {
  return [deal.contact?.firstName, deal.contact?.lastName].filter(Boolean).join(' ') || 'Контакт не указан';
}

export function DealListView({ deals, selectedIds, isUpdating, onToggleSelected, onMoveDeal }: DealListViewProps): ReactElement {
  const pagination = useDataViewPagination(deals);
  const columns: ColumnsType<DealListItem> = [
    {
      title: 'Сделка',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record) => (
        <Link to={`/deals/${record.id}`} style={{ fontWeight: 500, color: '#44403c' }}>
          {text}
        </Link>
      ),
    },
    {
      title: 'Контакт',
      key: 'contact',
      render: (_, record) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {getContactName(record)}
        </Text>
      ),
    },
    {
      title: 'Стадия',
      dataIndex: 'stage',
      key: 'stage',
      align: 'center',
      render: (stage: string) => (
        <Tag color={STAGE_COLORS[stage] ?? 'default'} style={{ margin: 0 }}>
          {stage}
        </Tag>
      ),
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: string) => (
        <Text style={{ fontWeight: 600, fontFamily: 'monospace', color: '#78716c' }}>
          {amount}
        </Text>
      ),
    },
    {
      title: 'Следующий шаг',
      key: 'nextStep',
      render: (_, record) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {record.nextActivityDue === null ? '—' : 'Запланирован'}
        </Text>
      ),
    },
    {
      title: 'Действие',
      key: 'action',
      align: 'center',
      render: (_, record) => {
        const nextStage = NEXT_STAGE[record.stage];
        if (nextStage !== undefined) {
          return (
            <Button
              size="small"
              aria-label={`Следующая стадия для ${record.title}`}
              disabled={isUpdating}
              onClick={(e) => {
                e.stopPropagation();
                onMoveDeal(record.id, nextStage);
              }}
              icon={<ArrowRightOutlined />}
            >
              {nextStage}
            </Button>
          );
        }
        return <Text type="secondary" style={{ fontSize: 11 }}>Финальная</Text>;
      },
    },
  ];

  return (
    <div style={{ background: '#fffdfa', border: '1px solid #e7e5e4', borderRadius: 8, overflow: 'hidden' }} data-testid="deals-list-view">
      <Table<DealListItem>
        rowSelection={{
          selectedRowKeys: Array.from(selectedIds),
          onChange: () => undefined,
          onSelect: (record) => {
            onToggleSelected(record.id);
          },
          onSelectAll: (_, __, changeRows) => {
            changeRows.forEach((record) => onToggleSelected(record.id));
          },
          getCheckboxProps: (record) => ({
            title: `Выбрать сделку ${record.title}`,
            'aria-label': `Выбрать сделку ${record.title}`,
          }),
        }}
        columns={columns}
        dataSource={deals}
        rowKey="id"
        size="small"
        pagination={pagination.pagination}
        style={{ fontSize: 13 }}
        onRow={() => ({
          onMouseEnter: (e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = '#fffbeb';
          },
          onMouseLeave: (e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
          },
        })}
      />
    </div>
  );
}
